import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Form, Row, Col, Button, Alert, Table, Modal } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney } from '../lib/format.js';

export default function SettingsPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = auth.can('settings', 'update');
  const canManageDunning = auth.can('dunning', 'manage_levels');

  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get('/settings').then(r => r.data.settings) });
  const dunningLevels = useQuery({ queryKey: ['dunning-levels'], queryFn: () => api.get('/dunning/levels').then(r => r.data.items) });

  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (settings.data && draft === null) setDraft({ ...settings.data });
  }, [settings.data, draft]);

  const save = useMutation({
    mutationFn: () => api.put('/settings', draft).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e) => setError(e.response?.data?.message || JSON.stringify(e.response?.data))
  });

  const set = (k) => (e) => setDraft(d => ({ ...d, [k]: typeof e === 'object' ? e.target.value : e }));
  const setNumber = (k) => (e) => setDraft(d => ({ ...d, [k]: parseFloat(e.target.value) || 0 }));
  const setBool = (k) => (e) => setDraft(d => ({ ...d, [k]: e.target.checked }));

  // ---- Dunning level editor ----
  const [lvlModal, setLvlModal] = useState(false);
  const [lvlDraft, setLvlDraft] = useState(null);
  const openLevel = (l) => {
    setLvlDraft(l ? {
      level: l.level, label: l.label,
      fee_cents: l.fee_cents,
      additional_grace_days: l.additional_grace_days,
      template: l.template || '',
      is_final: !!l.is_final
    } : { level: 1, label: '', fee_cents: 0, additional_grace_days: 7, template: '', is_final: false });
    setLvlModal(true);
  };
  const saveLevel = useMutation({
    mutationFn: () => {
      const body = { ...lvlDraft, fee_cents: parseInt(lvlDraft.fee_cents, 10) || 0 };
      return dunningLevels.data?.find(l => l.level === lvlDraft.level)
        ? api.patch(`/dunning/levels/${lvlDraft.level}`, body).then(r => r.data)
        : api.post('/dunning/levels', body).then(r => r.data);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dunning-levels'] }); setLvlModal(false); }
  });

  if (!draft) return <div className="text-muted">Lade…</div>;

  return (
    <>
      <PageHeader title="Einstellungen" subtitle="MwSt., Bankverbindung, Mahnwesen, Nummernkreise"
        actions={canWrite && <Button onClick={() => save.mutate()} disabled={save.isLoading}>
          {save.isLoading ? 'Speichere…' : 'Speichern'}
        </Button>} />

      {saved && <Alert variant="success" className="py-2">Gespeichert.</Alert>}
      {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

      <Row className="g-3 mb-3">
        <Col lg={6}>
          <Card className="card-flat h-100"><Card.Body>
            <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>Abrechnung</h6>
            <Row className="g-3">
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>Währung</Form.Label>
                  <Form.Control value={draft['billing.currency'] || 'EUR'} onChange={set('billing.currency')} disabled={!canWrite} />
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>MwSt.-Satz (%)</Form.Label>
                  <Form.Control type="number" step="0.5" value={draft['billing.vat_rate'] ?? 19}
                    onChange={setNumber('billing.vat_rate')} disabled={!canWrite} />
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>Zahlungsziel (Tage)</Form.Label>
                  <Form.Control type="number" min="0" value={draft['billing.payment_term_days'] ?? 14}
                    onChange={setNumber('billing.payment_term_days')} disabled={!canWrite} />
                </Form.Group>
              </Col>
            </Row>
            <h6 className="text-uppercase text-muted small mb-3 mt-4" style={{ letterSpacing: '0.05em' }}>Nummernkreise</h6>
            <Row className="g-3">
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>Kunde</Form.Label>
                  <Form.Control value={draft['billing.customer_prefix'] || 'K'} onChange={set('billing.customer_prefix')} disabled={!canWrite} />
                  <Form.Text>z.B. K-2026-0001</Form.Text>
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>Rechnung</Form.Label>
                  <Form.Control value={draft['billing.invoice_prefix'] || 'R'} onChange={set('billing.invoice_prefix')} disabled={!canWrite} />
                  <Form.Text>z.B. R-2026-0001</Form.Text>
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>Zahlung</Form.Label>
                  <Form.Control value={draft['billing.payment_prefix'] || 'Z'} onChange={set('billing.payment_prefix')} disabled={!canWrite} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body></Card>
        </Col>

        <Col lg={6}>
          <Card className="card-flat h-100"><Card.Body>
            <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>Rechnungssteller</h6>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control value={draft['billing.issuer_name'] || ''} onChange={set('billing.issuer_name')} disabled={!canWrite} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Adresse</Form.Label>
              <Form.Control as="textarea" rows={2} value={draft['billing.issuer_address'] || ''}
                onChange={set('billing.issuer_address')} disabled={!canWrite} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>USt-ID</Form.Label>
              <Form.Control value={draft['billing.issuer_vat_id'] || ''} onChange={set('billing.issuer_vat_id')} disabled={!canWrite} />
            </Form.Group>
            <h6 className="text-uppercase text-muted small mb-3 mt-3" style={{ letterSpacing: '0.05em' }}>Bankverbindung</h6>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Bank</Form.Label>
                  <Form.Control value={draft['billing.bank_name'] || ''} onChange={set('billing.bank_name')} disabled={!canWrite} />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>IBAN</Form.Label>
                  <Form.Control value={draft['billing.bank_iban'] || ''} onChange={set('billing.bank_iban')} disabled={!canWrite}
                    placeholder="DE89 3704 0044 0532 0130 00" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>BIC</Form.Label>
                  <Form.Control value={draft['billing.bank_bic'] || ''} onChange={set('billing.bank_bic')} disabled={!canWrite} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body></Card>
        </Col>
      </Row>

      <Card className="card-flat mb-3"><Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="text-uppercase text-muted small mb-0" style={{ letterSpacing: '0.05em' }}>E-Mail (SMTP)</h6>
          <Button size="sm" variant="outline-secondary" onClick={async () => {
            try {
              const r = await api.post('/settings/email/test');
              alert(r.data.ok ? 'SMTP-Verbindung OK.' : `Fehler: ${r.data.error || r.data.reason}`);
            } catch (e) { alert(`Fehler: ${e.message}`); }
          }}>
            <Icon icon="tabler:plug" className="me-1" />SMTP testen
          </Button>
        </div>
        <Row className="g-3">
          <Col md={6}><Form.Group>
            <Form.Label>SMTP-Server</Form.Label>
            <Form.Control value={draft['email.smtp_host'] || ''} onChange={set('email.smtp_host')}
              placeholder="smtp.beispiel.de" disabled={!canWrite} />
          </Form.Group></Col>
          <Col md={3}><Form.Group>
            <Form.Label>Port</Form.Label>
            <Form.Control type="number" value={draft['email.smtp_port'] || 587}
              onChange={(e) => setDraft(d => ({ ...d, 'email.smtp_port': parseInt(e.target.value, 10) || 587 }))}
              disabled={!canWrite} />
          </Form.Group></Col>
          <Col md={3}>
            <Form.Check type="switch" id="smtp-secure" className="mt-4"
              label="TLS (port 465)" checked={!!draft['email.smtp_secure']}
              onChange={(e) => setDraft(d => ({ ...d, 'email.smtp_secure': e.target.checked }))}
              disabled={!canWrite} />
          </Col>
          <Col md={6}><Form.Group>
            <Form.Label>Benutzer</Form.Label>
            <Form.Control value={draft['email.smtp_user'] || ''} onChange={set('email.smtp_user')} disabled={!canWrite} />
          </Form.Group></Col>
          <Col md={6}><Form.Group>
            <Form.Label>Passwort</Form.Label>
            <Form.Control type="password" value={draft['email.smtp_pass'] || ''} onChange={set('email.smtp_pass')}
              autoComplete="new-password" disabled={!canWrite} />
          </Form.Group></Col>
          <Col md={12}><Form.Group>
            <Form.Label>Absender (From)</Form.Label>
            <Form.Control value={draft['email.from'] || ''} onChange={set('email.from')}
              placeholder='"Firma" <rechnung@firma.de>' disabled={!canWrite} />
          </Form.Group></Col>
        </Row>
      </Card.Body></Card>

      <Card className="card-flat">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h6 className="text-uppercase text-muted small mb-1" style={{ letterSpacing: '0.05em' }}>Mahnwesen</h6>
              <div className="text-muted small">Mahnstufen werden in dieser Reihenfolge eskaliert.</div>
            </div>
            {canManageDunning && (
              <Button size="sm" onClick={() => openLevel(null)}><Icon icon="tabler:plus" className="me-1" />Mahnstufe</Button>
            )}
          </div>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Check type="switch" id="auto-advance"
                label="Automatisch mahnen"
                checked={!!draft['dunning.auto_advance']}
                onChange={setBool('dunning.auto_advance')}
                disabled={!canWrite} />
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Karenztage</Form.Label>
                <Form.Control type="number" min="0" value={draft['dunning.grace_days'] ?? 3}
                  onChange={setNumber('dunning.grace_days')} disabled={!canWrite} />
              </Form.Group>
            </Col>
          </Row>

          <Table hover className="mb-0 align-middle">
            <thead><tr><th>Stufe</th><th>Bezeichnung</th><th className="text-end">Gebühr</th><th>Karenz (Tage)</th><th>Final</th><th></th></tr></thead>
            <tbody>
              {(dunningLevels.data || []).map(l => (
                <tr key={l.id}>
                  <td><strong>{l.level}</strong></td>
                  <td>{l.label}</td>
                  <td className="text-end">{fmtMoney(l.fee_cents, draft['billing.currency'] || 'EUR')}</td>
                  <td>+{l.additional_grace_days}</td>
                  <td>{l.is_final ? <Icon icon="tabler:check" className="text-warning" /> : ''}</td>
                  <td className="text-end pe-3">
                    {canManageDunning && (
                      <Button size="sm" variant="outline-secondary" onClick={() => openLevel(l)}><Icon icon="tabler:edit" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Dunning level modal */}
      <Modal show={lvlModal} onHide={() => setLvlModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Mahnstufe</Modal.Title></Modal.Header>
        {lvlDraft && (
          <Modal.Body>
            <Row className="g-3">
              <Col xs={3}>
                <Form.Group>
                  <Form.Label>Stufe</Form.Label>
                  <Form.Control type="number" min="1" value={lvlDraft.level}
                    onChange={(e) => setLvlDraft({ ...lvlDraft, level: parseInt(e.target.value, 10) })} />
                </Form.Group>
              </Col>
              <Col xs={9}>
                <Form.Group>
                  <Form.Label>Bezeichnung</Form.Label>
                  <Form.Control value={lvlDraft.label}
                    onChange={(e) => setLvlDraft({ ...lvlDraft, label: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>Gebühr (Cent)</Form.Label>
                  <Form.Control type="number" min="0" value={lvlDraft.fee_cents}
                    onChange={(e) => setLvlDraft({ ...lvlDraft, fee_cents: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>Zusätzliche Karenz (Tage)</Form.Label>
                  <Form.Control type="number" min="0" value={lvlDraft.additional_grace_days}
                    onChange={(e) => setLvlDraft({ ...lvlDraft, additional_grace_days: parseInt(e.target.value, 10) })} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Check type="switch" label="Finale Stufe (z.B. Inkasso-Übergabe)"
                  checked={lvlDraft.is_final}
                  onChange={(e) => setLvlDraft({ ...lvlDraft, is_final: e.target.checked })} />
              </Col>
            </Row>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setLvlModal(false)}>Abbrechen</Button>
          <Button onClick={() => saveLevel.mutate()}>Speichern</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
